import React, { useMemo } from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import { getAllCommunities } from 'apis/CommunityAPI';
import { COMPANY_COMMUNITY_RELATION_TYPE } from 'constants/company';
import { PAGINATION_PAGE_SIZE } from 'constants/pagination';

import ShopRequestModalCommunityRow from './ShopRequestModalCommunityRow';
import { useShopRequestModalContext } from './ShopRequestModalContext';
import ShopRequestModalDropdown from './ShopRequestModalDropdown';
import { MYSTERY_SHOP_MODAL_REQUEST_FROM, SHOP_REQUEST_TYPE } from '../constants';

const Container = styled.div`
  box-sizing: border-box;
  flex: 1;
  min-height: 0;
  padding: 3px 3px 18px 30px;
  min-width: 600px;
  width: 100%;
`;

const ScrollContainer = styled.div`
  box-sizing: border-box;
  height: 100%;
  overflow-y: auto;
  padding: 15px 27px 0 0;
  scrollbar-gutter: stable;
`;

const ShopRequestModalCommunityRows = () => {
  const { requestFrom, requestType, shopRequests, selectedCompany, selectedBaseCommunity, selectCommunity } =
    useShopRequestModalContext();
  const isPricingRequest = requestType === SHOP_REQUEST_TYPE.MYSTERY_SHOP;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['company', 'competitors', selectedCompany, selectedBaseCommunity],
    queryFn: () =>
      getAllCommunities({
        companies: [selectedCompany],
        companies_relation: COMPANY_COMMUNITY_RELATION_TYPE.COMPETITOR,
        communities: [selectedBaseCommunity?.id],
        page_size: PAGINATION_PAGE_SIZE.LARGE,
      }),
    select: (data: any) => data?.results,
    enabled: !!selectedCompany && !!selectedBaseCommunity,
    refetchOnWindowFocus: false,
  });

  const remainingCommunities = useMemo(() => {
    if (!selectedBaseCommunity) {
      return [];
    }

    return (data ?? []).filter(
      (community: any) => !shopRequests.find((request) => request.communityId === community.id)
    );
  }, [selectedBaseCommunity, shopRequests, data]);

  const disableAddingCompetitors =
    requestFrom === MYSTERY_SHOP_MODAL_REQUEST_FROM.COMPANY && (!selectedCompany || !selectedBaseCommunity);

  return (
    <Container>
      <ScrollContainer className={'mini-scroll'}>
        {shopRequests.map((item: any) => {
          const shopRequestCommunity = (data ?? []).find((community: any) => community.id === item.communityId);
          return (
            <ShopRequestModalCommunityRow
              key={item.communityId}
              community={shopRequestCommunity}
              remainingCompetitors={remainingCommunities}
              shopRequest={item}
              loading={isLoading || isFetching}
              disabled={isLoading || isFetching}
            />
          );
        })}
        <ShopRequestModalDropdown
          communities={remainingCommunities}
          disabled={disableAddingCompetitors}
          value={null}
          onChange={selectCommunity}
          width={isPricingRequest ? '480px' : '100%'}
        />
      </ScrollContainer>
    </Container>
  );
};

export default ShopRequestModalCommunityRows;
